import { FC, ReactNode, RefObject, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { appConfig } from 'shared/appConfig';
import { mergeClasses } from 'shared/utils/mergeClasses';
import { useDebouncedCallback } from 'use-debounce';

const CloseMask: FC<{ onClick: () => void }> = ({ onClick }) => (
  // eslint-disable-next-line jsx-a11y/control-has-associated-label
  <button
    type="button"
    tabIndex={-1}
    onClick={(e) => {
      e.stopPropagation();
      e.preventDefault();
      onClick();
    }}
    className="fixed left-0 top-0 z-50 h-screen w-screen"
  />
);

const rightOffset = 16;
const getPositionStyles = (
  targetEl: HTMLElement | null,
  position: 'absolute' | 'fixed',
  topOffset: number,
  menuHeight: number,
  setWidth: boolean,
) => {
  if (!targetEl) {
    return {};
  }
  const { top, height, left, width } = targetEl.getBoundingClientRect();
  const parsedWidth = Math.max(150, width);

  const { scrollY } = window;
  const dropdownBottomEdgePosition = top + topOffset + menuHeight + height;

  const direction =
    dropdownBottomEdgePosition + scrollY > window.innerHeight + scrollY ? 'top' : 'bottom';
  const offsetY = direction === 'top' ? topOffset * -1 : topOffset;
  const offsetX =
    left + parsedWidth > window.innerWidth
      ? window.innerWidth - (left + parsedWidth + rightOffset)
      : 0;

  return {
    position,
    transform: position === 'absolute' ? `translate(${offsetX}px, ${offsetY}px)` : 'none',
    left: position === 'fixed' ? left : 'auto',
    ...(direction === 'top' &&
      position === 'fixed' && {
        bottom: window.innerHeight - top + topOffset,
      }),
    ...(direction === 'top' &&
      position === 'absolute' && {
        bottom: height,
      }),
    ...(direction === 'bottom' && {
      top: position === 'fixed' ? top + topOffset + height : 'auto',
    }),
    width: setWidth ? width : 'auto',
  };
};

interface DropdownWrapperProps {
  targetRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  onClose: () => void;
  position?: 'absolute' | 'fixed';
  topOffset?: number;
  menuHeight?: number;
  setWidth?: boolean;
  className?: string;
}

export const DropdownWrapper: FC<DropdownWrapperProps> = ({
  targetRef,
  position = 'absolute',
  topOffset = 6,
  menuHeight = 200,
  children,
  onClose,
  setWidth = true,
  className = '',
}) => {
  const [positionStyles, setPositionStyles] = useState({});
  const renderPortal = useCallback(
    (component: ReactNode) => {
      if (position === 'absolute') {
        return component;
      }

      const portalRoot = document.getElementById(appConfig.portalRootId);
      if (!portalRoot) {
        return null;
      }

      return createPortal(component, portalRoot);
    },
    [position],
  );

  const handleResize = useDebouncedCallback(
    () =>
      setPositionStyles(
        getPositionStyles(targetRef.current, position, topOffset, menuHeight, setWidth),
      ),
    50,
  );

  useEffect(() => {
    if (position === 'absolute') {
      return () => {};
    }
    window.addEventListener('scroll', handleResize);
    return () => {
      window.removeEventListener('scroll', handleResize);
    };
  }, []);

  useLayoutEffect(() => {
    setPositionStyles(
      getPositionStyles(targetRef.current, position, topOffset, menuHeight, setWidth),
    );
  }, [targetRef, position, topOffset]);

  return renderPortal(
    <>
      <CloseMask onClick={onClose} />
      <div className={mergeClasses('z-50', className)} style={positionStyles}>
        {children}
      </div>
    </>,
  );
};
